import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "palco_token";

export const APP_NAME = "Mí Chamou";
export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const setToken = (t) => { if (t) localStorage.setItem(TOKEN_KEY, t); };
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export function apiError(detail) {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  if (detail.message) return detail.message;
  if (detail.msg) return detail.msg;
  return "Algo deu errado. Tente novamente.";
}

export const LEVELS = ["iniciante", "intermediário", "avançado", "profissional"];

export const FEATURE_LABELS = {
  advanced_search: "Busca avançada de eventos", saved_searches: "Salvar buscas",
  event_alerts: "Alertas de eventos compatíveis", priority_notifications: "Notificação prioritária",
  cache_filter: "Filtro por cachê pretendido", min_cache_alerts: "Alertas por cachê mínimo",
  maestro_sync: "Repertório sincronizado / Maestro", complete_reports: "Relatórios completos",
  event_financial_result: "Resultado financeiro por evento", advanced_financial_history: "Histórico financeiro avançado",
  contractor_basic: "Buscar e convidar profissionais", contractor_advanced: "Recursos avançados de contratante",
  professional_search_priority: "Prioridade na busca de profissionais", priority_support: "Suporte prioritário",
  oncall: "Modo Plantão",
  repertoire: "Repertório (criar/ver/editar)", repertoire_share: "Compartilhar repertório",
  ranking_full: "Ranking Geral completo", minha_agenda: "Minha Agenda",
  export_excel: "Exportar Excel", export_csv: "Exportar CSV", export_pdf: "Exportar PDF",
};

export const STATUS_META = {
  "aguardando aprovação": { label: "Aguardando análise", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  "aprovado": { label: "Aprovada — aguardando confirmação", cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  "confirmado": { label: "Confirmada", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
  "concluido": { label: "Concluída", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  "reprovado": { label: "Não aprovada", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  "suplente": { label: "Suplente", cls: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
  "convidado": { label: "Convite recebido", cls: "bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/30" },
  "cancelado": { label: "Cancelada", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
  "expirado": { label: "Expirada", cls: "bg-zinc-600/20 text-zinc-400 border-zinc-500/30" },
};

export const EVENT_STATUS_META = {
  "aberto": { label: "Aberto", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  "preenchido": { label: "Preenchido", cls: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
  "concluido": { label: "Concluído", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
  "cancelado": { label: "Cancelado", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};

// Fase de ciclo de vida (item 41) — derivada do horário no backend, exibição amigável.
export const PHASE_META = {
  AGENDADO: { label: "Agendado", cls: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30" },
  EM_ANDAMENTO: { label: "Em andamento", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  CONCLUIDO: { label: "Concluído", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
  CANCELADO: { label: "Cancelado", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};

export function money(v) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
}

export function formatDate(d) {
  if (!d) return "—";
  try {
    const dt = typeof d === "string" && d.includes("T") ? new Date(d) : new Date(d + "T00:00:00");
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return d; }
}

export function formatDateTime(d) {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleString("pt-BR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return d; }
}

export function whatsappUrl(ev) {
  const link = `${window.location.origin}/eventos/${ev.id}`;
  const lines = [
    `🎵 *${ev.name}* — vaga no Mí Chamou`,
    `📅 ${formatDate(ev.date)} · ${ev.start_time}${ev.end_time ? `–${ev.end_time}` : ""}`,
    ev.address ? `📍 ${ev.address}` : null,
    (ev.cache != null && ev.cache !== "") ? `💰 Cachê: ${money(ev.cache)}` : null,
    (ev.instruments && ev.instruments.length) ? `🎸 ${ev.instruments.join(", ")}` : null,
    "",
    "Candidate-se pela plataforma:",
    link,
  ].filter((x) => x != null);
  return `https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`;
}

export function mapsUrl(ev) {
  if (ev?.coords?.lat != null) return `https://www.google.com/maps/search/?api=1&query=${ev.coords.lat},${ev.coords.lng}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ev?.address || "")}`;
}

export function wazeUrl(ev) {
  if (ev?.coords?.lat != null) return `https://waze.com/ul?ll=${ev.coords.lat},${ev.coords.lng}&navigate=yes`;
  return `https://waze.com/ul?q=${encodeURIComponent(ev?.address || "")}&navigate=yes`;
}

export function getGeo() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Geolocalização não suportada"));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });
}

export function haversineKm(a, b) {
  if (!a || !b || a.lat == null || b.lat == null) return null;
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}
